import React, { useRef, useEffect, useState, useCallback, useContext, useMemo, use } from 'react';
import { LayoutContext } from '../layout-context-container';

const CustomScrollbar = ({ className = '' , contentContainerRef, enableCustomScrollbar = null, onDrag = null, ...rest }) => {
  const {isMobile} = useContext(LayoutContext);
  const contentRef = useRef(contentContainerRef?.current || null);
  const thumbRef = useRef(null);
  const trackRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [scrollbarVisible, setScrollbarVisible] = useState(false);

  useEffect(() => {
    onDrag && onDrag(isDragging);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging]);

  const updateScrollbar = useCallback(() => {
    if (!contentRef.current || !thumbRef.current || !trackRef.current || isMobile) return;

    const content = contentRef.current;
    const thumb = thumbRef.current;
    const track = trackRef.current;

    const contentHeight = content.offsetHeight;
    const scrollHeight = content.scrollHeight;
    const scrollTop = content.scrollTop;

    // Show/hide scrollbar based on content overflow
    const hasOverflow = scrollHeight > contentHeight;
    setScrollbarVisible(hasOverflow);

    if (!hasOverflow) return;

    // Calculate thumb height and position
    const thumbHeight = Math.max(20, (contentHeight / scrollHeight) * track.offsetHeight);
    const scrollRatio = scrollTop / (scrollHeight - contentHeight);
    const maxThumbTop = track.offsetHeight - thumbHeight;
    const thumbTop = scrollRatio * maxThumbTop;

    thumb.style.height = `${thumbHeight}px`;
    thumb.style.transform = `translateY(${thumbTop}px)`;
  }, [isMobile]);

  const handleScroll = useCallback(() => {
    updateScrollbar();
  }, [updateScrollbar]);

  const handleMouseDown = useCallback((e) => {
    if (isMobile) return;
    
    e.preventDefault();
    e.stopPropagation();

    setIsDragging(true);

    const startY = e.clientY;
    const startScrollTop = contentRef.current.scrollTop;

    const handleMouseMove = (e) => {
      if (!contentRef.current || !thumbRef.current || !trackRef.current) return;

      const deltaY = e.clientY - startY;
      const trackHeight = trackRef.current.offsetHeight;
      const thumbHeight = thumbRef.current.offsetHeight;
      const scrollableHeight = contentRef.current.scrollHeight - contentRef.current.offsetHeight;

      const scrollRatio = deltaY / (trackHeight - thumbHeight);
      
      const targetTop = startScrollTop + (scrollRatio * scrollableHeight);
      let currentTop = contentRef.current.scrollTop;
      const easeFactor = 0.11; // Adjust for more/less easing
      
      const animateScroll = () => {
        const diff = targetTop - currentTop;
        if (Math.abs(diff) > 0.5) {
          currentTop += diff * easeFactor;
          contentRef.current.scrollTo({ top: currentTop, behavior: "instant" });
          requestAnimationFrame(animateScroll);
        } else {
          contentRef.current.scrollTo({ top: targetTop, behavior: "instant" });
        }
      };
      
      animateScroll();
    };

    const handleMouseUp = (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }
  }, [isMobile]);

  const handleTrackClick = useCallback((e) => {
    if (isMobile || e.target === thumbRef.current) return;

    const track = trackRef.current;
    const content = contentRef.current;
    const thumb = thumbRef.current;

    const trackRect = track.getBoundingClientRect();
    const clickY = e.clientY - trackRect.top;
    const trackHeight = track.offsetHeight;
    const thumbHeight = thumb.offsetHeight;
    const scrollableHeight = content.scrollHeight - content.offsetHeight;

    const scrollRatio = (clickY - thumbHeight / 2) / (trackHeight - thumbHeight);
    content.scrollTop = Math.max(0, Math.min(scrollableHeight, scrollRatio * scrollableHeight));
  }, [isMobile]);

  // Touch events for mobile/tablet
  const handleTouchStart = useCallback((e) => {
    e.preventDefault();
    const touch = e.touches[0];
    const startY = touch.clientY;
    const startScrollTop = contentRef.current.scrollTop;

    const handleTouchMove = (e) => {
      if (!contentRef.current || !thumbRef.current || !trackRef.current) return;

      const touch = e.touches[0];
      const deltaY = touch.clientY - startY;
      const trackHeight = trackRef.current.offsetHeight;
      const thumbHeight = thumbRef.current.offsetHeight;
      const scrollableHeight = contentRef.current.scrollHeight - contentRef.current.offsetHeight;

      const scrollRatio = deltaY / (trackHeight - thumbHeight);

      const targetTop = startScrollTop + (scrollRatio * scrollableHeight);
      let currentTop = contentRef.current.scrollTop;
      const easeFactor = 0.15; // Adjust for more/less easing
      
      const animateScroll = () => {
        const diff = targetTop - currentTop;
        if (Math.abs(diff) > 0.5) {
          currentTop += diff * easeFactor;
          contentRef.current.scrollTo({ top: currentTop, behavior: "instant" });
          requestAnimationFrame(animateScroll);
        } else {
          contentRef.current.scrollTo({ top: targetTop, behavior: "instant" });
        }
      };
      
      animateScroll();
    };

    const handleTouchEnd = () => {
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };

    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    }
  }, []);

  // Add scroll and wheel event listeners
  useEffect(() => {
    const content = contentRef.current;
    if (content) {
      setTimeout(() => handleScroll(), 0); // Initial update
      content.addEventListener('scroll', handleScroll);
      return () => content.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  useEffect(() => {
    updateScrollbar();
  }, [updateScrollbar]);

  const noStyleRest = useMemo(() => {
    const newRest = { ...rest };
    if (newRest.style) {
      delete newRest.style;
    }
    return newRest;
  }, [rest]);

  return (
    <div className="custom-scrollbar" style={{
        maxHeight: enableCustomScrollbar ? `${enableCustomScrollbar}px` : 'none', ...rest?.style,
        display: scrollbarVisible ? 'block' : 'none',
        visibility: scrollbarVisible ? 'visible' : 'hidden',
      }} {...noStyleRest}>
      {!isMobile && (
        <div
          ref={trackRef}
          className={`custom-scrollbar__track ${isDragging ? 'custom-scrollbar__track--dragging' : ''} ${className}`}
          onClick={handleTrackClick}
        >
          <div
            ref={thumbRef}
            className="custom-scrollbar__thumb"
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            style={{
              cursor: isDragging ? 'grabbing' : 'grab',
            }}
          />
        </div>
      )}
    </div>
  );
};

export default CustomScrollbar;